/**
 * Product Card Video Hover Component
 * Handles video playback on product card hover with enter/exit animations
 */

class ProductCardVideo extends HTMLElement {
  constructor() {
    super();
    this.video = this.querySelector('video');
    this.card = this.closest('.product-card__wrapper');
    this.isHovering = false;
    this.hasPlayedEnterAnimation = false;
    this.pauseTimeout = null;
    this.isInitialized = false;
    
    // Configuration from data attributes with defaults
    // ⚙️ EDIT THESE VALUES TO CHANGE TIMING FOR ALL VIDEOS
    this.enterDuration = parseFloat(this.dataset.enterDuration) || 1.8; // seconds - how long enter animation is (0 to 1.5s)
    this.pauseDuration = parseFloat(this.dataset.pauseDuration) || 2; // seconds to pause after enter animation (not used for timing, just for reference)
    this.exitDuration = parseFloat(this.dataset.exitDuration) || 1.2; // seconds - how long exit animation is (1.5s to 3.5s)
    
    // Calculate when exit animation starts (right after enter)
    this.exitStartTime = this.enterDuration;
    // Calculate when video should stop (enter + exit)
    this.videoEndTime = this.enterDuration + this.exitDuration;
    
    if (this.video && this.card) {
      this.checkAndInit();
      // Listen for window resize to re-evaluate mobile state
      window.addEventListener('resize', this.handleResize.bind(this));
    }
  }

  isMobile() {
    return window.innerWidth <= 768;
  }

  checkAndInit() {
    if (this.isMobile()) {
      this.cleanup();
    } else {
      if (!this.isInitialized) {
        this.init();
      }
    }
  }

  init() {
    if (this.isInitialized) return;
    
    // Set video to first frame
    this.video.currentTime = 0;
    
    // Bind hover events to the card wrapper
    this.card.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
    this.card.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
    
    // Listen for video timeupdate to pause at the right moment
    this.video.addEventListener('timeupdate', this.handleTimeUpdate.bind(this));
    
    // Listen for video end to reset
    this.video.addEventListener('ended', this.handleVideoEnd.bind(this));
    
    // Ensure video is loaded
    this.video.load();
    
    this.isInitialized = true;
  }

  cleanup() {
    if (!this.isInitialized) return;
    
    // Remove event listeners
    this.card.removeEventListener('mouseenter', this.handleMouseEnter.bind(this));
    this.card.removeEventListener('mouseleave', this.handleMouseLeave.bind(this));
    this.video.removeEventListener('timeupdate', this.handleTimeUpdate.bind(this));
    this.video.removeEventListener('ended', this.handleVideoEnd.bind(this));
    
    // Reset video state
    this.video.pause();
    this.video.currentTime = 0;
    this.classList.remove('is-active');
    this.isHovering = false;
    this.hasPlayedEnterAnimation = false;
    
    if (this.pauseTimeout) {
      clearTimeout(this.pauseTimeout);
      this.pauseTimeout = null;
    }
    
    this.isInitialized = false;
  }

  handleResize() {
    // Debounce resize events
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.checkAndInit();
    }, 250);
  }

  handleMouseEnter() {
    this.isHovering = true;
    this.hasPlayedEnterAnimation = false;
    
    // Clear any existing pause timeout
    if (this.pauseTimeout) {
      clearTimeout(this.pauseTimeout);
      this.pauseTimeout = null;
    }
    
    // Show video and hide main image
    this.classList.add('is-active');
    
    // Reset to start and play
    this.video.currentTime = 0;
    this.video.play().catch(err => {
      console.warn('Video play failed:', err);
    });
  }

  handleMouseLeave() {
    this.isHovering = false;
    
    // Clear any existing pause timeout
    if (this.pauseTimeout) {
      clearTimeout(this.pauseTimeout);
      this.pauseTimeout = null;
    }
    
    // If we're in the paused state (after enter animation), play the exit animation
    if (this.hasPlayedEnterAnimation && this.video.paused) {
      // Video is paused at 1s, now continue to play from 1s to 2s (exit animation)
      this.video.play().catch(err => {
        console.warn('Video play failed:', err);
      });
    } else if (!this.hasPlayedEnterAnimation) {
      // If still playing enter animation, let it continue to exit
      // The timeUpdate handler will stop it at 2s
    }
  }

  handleTimeUpdate() {
    const currentTime = this.video.currentTime;
    
    // If hovering and we've reached the end of the enter animation (at 1s)
    if (this.isHovering && !this.hasPlayedEnterAnimation && currentTime >= this.enterDuration) {
      this.hasPlayedEnterAnimation = true;
      
      // Pause the video at the end of enter animation
      this.video.pause();
    }
    
    // If NOT hovering and we've completed the exit animation
    if (!this.isHovering && currentTime >= this.videoEndTime) {
      // Hide video and show image FIRST (prevents blink)
      this.classList.remove('is-active');
      this.hasPlayedEnterAnimation = false;
      
      // Then reset video (do this after showing image to prevent flash)
      setTimeout(() => {
        this.video.pause();
        this.video.currentTime = 0;
      }, 0);
    }
  }

  handleVideoEnd() {
    // Fallback: If video somehow plays to the very end
    // Reset everything
    this.video.currentTime = 0;
    this.video.pause();
    
    // Hide video and show image again
    this.classList.remove('is-active');
    this.hasPlayedEnterAnimation = false;
  }

  disconnectedCallback() {
    // Clean up resize listener when element is removed
    window.removeEventListener('resize', this.handleResize.bind(this));
    this.cleanup();
  }
}

customElements.define('product-card-video', ProductCardVideo);

